<section class="relative flex flex-col items-center w-full min-w-0 overflow-x-hidden">
  <div class="relative flex flex-col w-full max-w-5xl gap-6 px-2 sm:px-3">
    <div>
      <h1 class="font-display text-2xl md:text-3xl font-bold text-gray-900">QR codes Wallet</h1>
      <p class="text-gray-500 mt-1">
        Envoyez à chaque client (email de contact) les QR codes Apple Wallet et Google Wallet,
        avec le nom et le téléphone de votre cabinet.
      </p>
    </div>

    @if (loading()) {
      <article class="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">
        Chargement...
      </article>
    } @else if (!hasAccess()) {
      <article class="rounded-xl border border-amber-200 bg-amber-50 p-6 space-y-3">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-amber-600 text-3xl">workspace_premium</span>
          <div class="space-y-2">
            <h2 class="font-semibold text-amber-900">Option non activée</h2>
            <p class="text-sm text-amber-900/90">
              Cette fonctionnalité nécessite l'abonnement Stripe « envoi QR Wallet » pour les experts-comptables.
              Dès que le produit sera disponible, vous pourrez l'activer depuis votre espace.
            </p>
          </div>
        </div>
      </article>
    } @else {
      <article class="rounded-xl border border-sky-200 bg-sky-50 p-5 space-y-3">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-sky-700 text-2xl shrink-0">mail</span>
          <div class="space-y-1 min-w-0">
            <h2 class="font-semibold text-sky-950">Destinataires</h2>
            <p class="text-sm text-sky-900/90">
              Les QR codes seront envoyés aux <strong>emails de contact</strong> renseignés pour chaque client.<br>
              <strong>Les entreprises sans email de contact seront ignorées.</strong>
            </p>
          </div>
        </div>

        @if (recipientsLoading()) {
          <p class="text-sm text-sky-800/80">Chargement des exemples…</p>
        } @else if (recipientsPreview().length === 0) {
          <p class="text-sm text-sky-800/80">
            Aucun client pour le moment.
            <a routerLink="/dashboard/expert/clients" class="underline font-medium">Ajouter des clients</a>
          </p>
        } @else {
          <div class="rounded-lg border border-sky-100 bg-white overflow-hidden">
            <p class="px-3 py-2 text-xs font-medium text-sky-900/80 border-b border-sky-100">
              Exemple — 10 premiers clients
            </p>
            <ul class="divide-y divide-sky-50 text-sm">
              @for (recipient of recipientsPreview(); track recipient.siret) {
                <li class="px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 min-w-0">
                  <span class="font-medium text-gray-900 truncate">{{ recipient.companyName }}</span>
                  @if (recipient.contactMail) {
                    <span class="text-gray-600 truncate font-mono text-xs sm:text-sm">
                      {{ recipient.contactMail }}
                      @if (recipient.contactName) {
                        <span class="text-gray-400 font-sans"> · {{ recipient.contactName }}</span>
                      }
                    </span>
                  } @else {
                    <span class="text-amber-700 text-xs sm:text-sm">Email de contact non renseigné</span>
                  }
                </li>
              }
            </ul>
          </div>
          <p class="text-xs text-sky-900/70">
            Pour modifier un contact :
            <a routerLink="/dashboard/expert/clients" class="underline font-medium">Mes clients</a>
          </p>
        }
      </article>

      <article class="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <h2 class="font-semibold text-gray-900 flex items-center gap-2">
          <span class="material-symbols-outlined text-secondary">qr_code_2</span>
          Envoi groupé
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div class="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <p class="text-xs text-gray-500">Nom du cabinet</p>
            @if (hasCabinetName()) {
              <p class="font-medium text-gray-900 mt-0.5">{{ cabinetName() }}</p>
            } @else {
              <p class="text-amber-700 mt-0.5">Non renseigné</p>
            }
          </div>
          <div class="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <p class="text-xs text-gray-500">Téléphone du cabinet</p>
            @if (hasCabinetPhone()) {
              <p class="font-medium text-gray-900 mt-0.5">{{ cabinetPhone() }}</p>
            } @else {
              <p class="text-amber-700 mt-0.5">Non renseigné</p>
            }
          </div>
          <div class="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <p class="text-xs text-gray-500">Email du cabinet</p>
            @if (hasCabinetEmail()) {
              <p class="font-medium text-gray-900 mt-0.5 truncate">{{ cabinetEmail() }}</p>
            } @else {
              <p class="text-amber-700 mt-0.5">Non renseigné</p>
            }
          </div>
        </div>
        @if (!cabinetComplete()) {
          <p class="text-sm text-amber-900">
            Le nom, le téléphone et l'email du cabinet sont obligatoires pour envoyer les QR codes.
            <a routerLink="/dashboard/expert/parametres" class="underline font-medium">Compléter les paramètres</a>
          </p>
        } @else {
          <a routerLink="/dashboard/expert/parametres" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
            <span class="material-symbols-outlined text-[18px]">settings</span>
            Modifier dans les paramètres
          </a>
        }
        <ul class="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Un email est envoyé à l'<strong>adresse de contact</strong> de chaque entreprise.</li>
          <li>Chaque email contient deux QR codes : <strong>Apple Wallet</strong> et <strong>Google Wallet</strong>.</li>
          <li>Le <strong>nom</strong>, le <strong>téléphone</strong> et l'<strong>email</strong> du cabinet apparaissent au verso / dans les détails des cartes Wallet.</li>
        </ul>
        <div class="flex flex-wrap gap-3">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-lg bg-secondary px-4 py-2.5 text-sm font-medium text-white hover:bg-secondary-hover cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            [disabled]="!canSend()"
            (click)="sendAll()"
          >
            <span class="material-symbols-outlined text-[18px]" [class.animate-spin]="sending()">
              {{ sending() ? 'progress_activity' : 'send' }}
            </span>
            {{ sending() ? 'Envoi en cours…' : 'Envoyer à tous les clients' }}
          </button>
        </div>
      </article>

      @if (lastResult(); as result) {
        <article class="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
          <h2 class="font-semibold text-gray-900">Résultat</h2>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div class="rounded-lg bg-green-50 border border-green-100 px-3 py-2">
              <p class="text-green-800 font-medium">{{ result.sent }} envoyé(s)</p>
            </div>
            <div class="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <p class="text-amber-800 font-medium">{{ result.skippedNoContact }} sans contact</p>
            </div>
            <div class="rounded-lg bg-red-50 border border-red-100 px-3 py-2">
              <p class="text-red-800 font-medium">{{ result.failed }} échec(s)</p>
            </div>
          </div>
          @if (result.errors.length > 0) {
            <ul class="text-xs text-red-700 space-y-1 list-disc list-inside">
              @for (err of result.errors; track err) {
                <li>{{ err }}</li>
              }
            </ul>
          }
        </article>
      }
    }
  </div>
</section>
